import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "../api/axios";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  FormFeedback,
} from "reactstrap";
import { useDropzone } from "react-dropzone";

export default function Company(props) {
  const EDIT_COMPANY_URL = "/company/edit";

  const [formData, setFormData] = useState({
    companyID: props.id,
    adminID: props.adminID,
    name: props.name || "",
    ownerFirstName: props.ownerFirstName || "",
    ownerLastName: props.ownerLastName || "",
    address1: props.addresses?.[0] || "",
    address2: props.addresses?.[1] || "",
    city: props.city || "",
    state: props.state || "",
    zip: props.zip || "",
    email: props.email || "",
    phone: props.phone || "",
    domain: props.domain || "",

    chatbotsModuleEnabled: props.chatbotsModuleEnabled ?? false,
    maxChatbotsAllowed: props.maxChatbotsAllowed ?? 0,
    chatbotPlan: props.chatbotPlan ?? "basic",

    voiceAssistantModuleEnabled: props.voiceAssistantModuleEnabled ?? false,
    maxVoiceAssistantsAllowed: props.maxVoiceAssistantsAllowed ?? 0,
    voiceAssistantPlan: props.voiceAssistantPlan ?? "basic",
    voiceMaxCallsPerDay: props.voiceMaxCallsPerDay ?? 50,
    voiceMaxTokensPerDay: props.voiceMaxTokensPerDay ?? 200000,
    voiceMaxConcurrentCalls: props.voiceMaxConcurrentCalls ?? 5,
    voiceMaxCallDurationSeconds: props.voiceMaxCallDurationSeconds ?? 600,
    voiceMaxTTSCharsPerDay: props.voiceMaxTTSCharsPerDay ?? 500000,
    voiceMaxSTTSecondsPerDay: props.voiceMaxSTTSecondsPerDay ?? 18000,
    voiceUsageWarningThresholdPct: props.voiceUsageWarningThresholdPct ?? 80,
    voiceRecordingEnabled: props.voiceRecordingEnabled ?? false,
    voiceModuleSuspended: props.voiceModuleSuspended ?? false,
    voiceModuleSuspendedReason: props.voiceModuleSuspendedReason ?? "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [modal, setModal] = useState(false);
  const [errors, setErrors] = useState({});

  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".gif"] },
    maxFiles: 1,
    onDrop: (files) => setLogoFile(files[0]),
  });

  const toggleModal = () => setModal(!modal);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Company name is required";
    if (!formData.ownerFirstName.trim()) newErrors.ownerFirstName = "First name is required";
    if (!formData.ownerLastName.trim()) newErrors.ownerLastName = "Last name is required";
    if (!formData.address1.trim()) newErrors.address1 = "Address 1 is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.zip.trim()) newErrors.zip = "Zip code is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Enter a valid email";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid phone number";
    }
    if (!formData.domain.trim()) newErrors.domain = "Domain is required";
    else if (!/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.domain)) {
      newErrors.domain = "Enter a valid domain";
    }

    if (formData.maxChatbotsAllowed < 0 || isNaN(formData.maxChatbotsAllowed)) {
      newErrors.maxChatbotsAllowed = "Must be a non-negative number";
    }
    if (formData.maxVoiceAssistantsAllowed < 0 || isNaN(formData.maxVoiceAssistantsAllowed)) {
      newErrors.maxVoiceAssistantsAllowed = "Must be a non-negative number";
    }
    if (
      formData.voiceUsageWarningThresholdPct < 0 ||
      formData.voiceUsageWarningThresholdPct > 100 ||
      isNaN(formData.voiceUsageWarningThresholdPct)
    ) {
      newErrors.voiceUsageWarningThresholdPct = "Must be between 0 and 100";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onModalSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError("");

    const token = window.localStorage.getItem("accessToken");

    try {
      const requestBody = {
        ...formData,
        address: [formData.address1, formData.address2],
      };

      const response = await axios.put(EDIT_COMPANY_URL, requestBody, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === 201) {
        if (logoFile) {
          const formDataUpload = new FormData();
          formDataUpload.append("file", logoFile);
          await axios.post("/company/upload-documents", formDataUpload, {
            headers: {
              "Content-Type": "multipart/form-data",
              id: response.data.id,
            },
          });
        }
        window.location.reload();
      } else {
        setSubmitError(response.data.message || "Failed to update company");
      }
    } catch (error) {
      setSubmitError(error.response?.data?.message || "An error occurred while saving");
    } finally {
      setIsSubmitting(false);
    }
  };

  async function deleteCompany(companyID, adminID) {
    if (window.confirm("Are you sure you want to delete this company?")) {
      try {
        const response = await axios.delete(
          `/company/delete?companyID=${companyID}&adminID=${adminID}`
        );
        if (response.data.status === 200) {
          window.location.replace("/company");
        }
      } catch (error) {
        alert("Failed to delete company");
      }
    }
  }

  return (
    <>
      <Modal size="xl" isOpen={modal} toggle={toggleModal} backdrop="static">
        <ModalHeader toggle={toggleModal}>Edit Company</ModalHeader>
        <ModalBody>
          <Form onSubmit={onModalSave}>
            <Row>
              {/* Company Information */}
              <Col md={6}>
                <FormGroup>
                  <Label for="name">Company Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    invalid={!!errors.name}
                  />
                  <FormFeedback>{errors.name}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="ownerFirstName">Owner First Name *</Label>
                  <Input
                    id="ownerFirstName"
                    name="ownerFirstName"
                    value={formData.ownerFirstName}
                    onChange={handleInputChange}
                    invalid={!!errors.ownerFirstName}
                  />
                  <FormFeedback>{errors.ownerFirstName}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="ownerLastName">Owner Last Name *</Label>
                  <Input
                    id="ownerLastName"
                    name="ownerLastName"
                    value={formData.ownerLastName}
                    onChange={handleInputChange}
                    invalid={!!errors.ownerLastName}
                  />
                  <FormFeedback>{errors.ownerLastName}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label for="address1">Address 1 *</Label>
                  <Input
                    id="address1"
                    name="address1"
                    value={formData.address1}
                    onChange={handleInputChange}
                    invalid={!!errors.address1}
                  />
                  <FormFeedback>{errors.address1}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="address2">Address 2</Label>
                  <Input
                    id="address2"
                    name="address2"
                    value={formData.address2}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label for="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    invalid={!!errors.city}
                  />
                  <FormFeedback>{errors.city}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    invalid={!!errors.state}
                  />
                  <FormFeedback>{errors.state}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="zip">Zip Code *</Label>
                  <Input
                    id="zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    invalid={!!errors.zip}
                  />
                  <FormFeedback>{errors.zip}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label for="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    invalid={!!errors.email}
                  />
                  <FormFeedback>{errors.email}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    invalid={!!errors.phone}
                  />
                  <FormFeedback>{errors.phone}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label for="domain">Domain *</Label>
                  <Input
                    id="domain"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    invalid={!!errors.domain}
                  />
                  <FormFeedback>{errors.domain}</FormFeedback>
                </FormGroup>
              </Col>

              {/* Logo Upload */}
              <Col md={12}>
                <FormGroup>
                  <Label>Company Logo</Label>
                  <div
                    {...getRootProps()}
                    className="border border-dashed rounded p-5 text-center bg-light"
                    style={{ cursor: "pointer", minHeight: "140px" }}
                  >
                    <input {...getInputProps()} />
                    {logoFile ? (
                      <div className="text-success">
                        <i className="bx bx-check-circle fs-1 d-block mb-2"></i>
                        <p className="mb-0 fw-bold">{logoFile.name}</p>
                        <small>Click to change file</small>
                      </div>
                    ) : props?.logo ? (
                      <div>
                        <i className="bx bx-image fs-1 text-muted d-block mb-2"></i>
                        <p className="mb-1">Current logo exists</p>
                        <small className="text-muted">Click to replace</small>
                      </div>
                    ) : (
                      <div>
                        <i className="bx bx-cloud-upload fs-1 text-muted d-block mb-2"></i>
                        <p className="mb-1">Drop image here or click to upload</p>
                        <small className="text-muted">PNG, JPG, GIF only</small>
                      </div>
                    )}
                  </div>
                </FormGroup>
              </Col>

              {/* Chatbot Module */}
              <Col xs={12}>
                <h5 className="mt-4 mb-3 border-bottom pb-2">Chatbot Module Settings</h5>
              </Col>

              <Col md={4}>
                <FormGroup check className="mt-2">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="chatbotsModuleEnabled"
                      checked={formData.chatbotsModuleEnabled}
                      onChange={handleInputChange}
                    />{" "}
                    Enable Chatbot Module
                  </Label>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="maxChatbotsAllowed">Max Chatbots Allowed</Label>
                  <Input
                    type="number"
                    id="maxChatbotsAllowed"
                    name="maxChatbotsAllowed"
                    value={formData.maxChatbotsAllowed}
                    onChange={handleInputChange}
                    min="0"
                    invalid={!!errors.maxChatbotsAllowed}
                  />
                  <FormFeedback>{errors.maxChatbotsAllowed}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="chatbotPlan">Chatbot Plan</Label>
                  <Input
                    type="select"
                    id="chatbotPlan"
                    name="chatbotPlan"
                    value={formData.chatbotPlan}
                    onChange={handleInputChange}
                  >
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </Input>
                </FormGroup>
              </Col>

              {/* Voice Assistant Module */}
              <Col xs={12}>
                <h5 className="mt-4 mb-3 border-bottom pb-2">Voice Assistant Module Settings</h5>
              </Col>

              <Col md={4}>
                <FormGroup check className="mt-2">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="voiceAssistantModuleEnabled"
                      checked={formData.voiceAssistantModuleEnabled}
                      onChange={handleInputChange}
                    />{" "}
                    Enable Voice Assistant
                  </Label>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="maxVoiceAssistantsAllowed">Max Voice Assistants Allowed</Label>
                  <Input
                    type="number"
                    id="maxVoiceAssistantsAllowed"
                    name="maxVoiceAssistantsAllowed"
                    value={formData.maxVoiceAssistantsAllowed}
                    onChange={handleInputChange}
                    min="0"
                    invalid={!!errors.maxVoiceAssistantsAllowed}
                  />
                  <FormFeedback>{errors.maxVoiceAssistantsAllowed}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="voiceAssistantPlan">Voice Assistant Plan</Label>
                  <Input
                    type="select"
                    id="voiceAssistantPlan"
                    name="voiceAssistantPlan"
                    value={formData.voiceAssistantPlan}
                    onChange={handleInputChange}
                  >
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label for="voiceMaxCallsPerDay">Max Calls / Day</Label>
                  <Input
                    type="number"
                    id="voiceMaxCallsPerDay"
                    name="voiceMaxCallsPerDay"
                    value={formData.voiceMaxCallsPerDay}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="voiceMaxTokensPerDay">Max Tokens / Day</Label>
                  <Input
                    type="number"
                    id="voiceMaxTokensPerDay"
                    name="voiceMaxTokensPerDay"
                    value={formData.voiceMaxTokensPerDay}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="voiceMaxConcurrentCalls">Max Concurrent Calls</Label>
                  <Input
                    type="number"
                    id="voiceMaxConcurrentCalls"
                    name="voiceMaxConcurrentCalls"
                    value={formData.voiceMaxConcurrentCalls}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="voiceMaxCallDurationSeconds">Max Call Duration (sec)</Label>
                  <Input
                    type="number"
                    id="voiceMaxCallDurationSeconds"
                    name="voiceMaxCallDurationSeconds"
                    value={formData.voiceMaxCallDurationSeconds}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label for="voiceMaxTTSCharsPerDay">Max TTS Chars / Day</Label>
                  <Input
                    type="number"
                    id="voiceMaxTTSCharsPerDay"
                    name="voiceMaxTTSCharsPerDay"
                    value={formData.voiceMaxTTSCharsPerDay}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="voiceMaxSTTSecondsPerDay">Max STT Seconds / Day</Label>
                  <Input
                    type="number"
                    id="voiceMaxSTTSecondsPerDay"
                    name="voiceMaxSTTSecondsPerDay"
                    value={formData.voiceMaxSTTSecondsPerDay}
                    onChange={handleInputChange}
                    min="0"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="voiceUsageWarningThresholdPct">Usage Warning %</Label>
                  <Input
                    type="number"
                    id="voiceUsageWarningThresholdPct"
                    name="voiceUsageWarningThresholdPct"
                    value={formData.voiceUsageWarningThresholdPct}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    invalid={!!errors.voiceUsageWarningThresholdPct}
                  />
                  <FormFeedback>{errors.voiceUsageWarningThresholdPct}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup check className="mt-4">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="voiceRecordingEnabled"
                      checked={formData.voiceRecordingEnabled}
                      onChange={handleInputChange}
                    />{" "}
                    Enable Recording
                  </Label>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup check className="mt-4">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="voiceModuleSuspended"
                      checked={formData.voiceModuleSuspended}
                      onChange={handleInputChange}
                    />{" "}
                    Suspend Module
                  </Label>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="voiceModuleSuspendedReason">Suspension Reason</Label>
                  <Input
                    id="voiceModuleSuspendedReason"
                    name="voiceModuleSuspendedReason"
                    value={formData.voiceModuleSuspendedReason}
                    onChange={handleInputChange}
                    placeholder="Optional reason for suspension"
                  />
                </FormGroup>
              </Col>
            </Row>

            {submitError && <Alert color="danger" className="mt-3">{submitError}</Alert>}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="success" onClick={onModalSave} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" /> Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <tr>
        <td>{props.number}</td>
        <td>
          <Link to="#" className="fw-bold text-decoration-none text-primary">
            {props.name}
          </Link>
        </td>
        <td>{props.owner}</td>
        <td>{props.email}</td>
        <td>{props.domain}</td>
        <td>{props.city}</td>
        <td>{props.state}</td>
        <td className="text-center">
          {props?.logo ? (
            <Button
              color="soft-light"
              size="sm"
              onClick={() => window.open(props.logo, "_blank")}
            >
              <i className="bx bx-download"></i>
            </Button>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="text-center">
          <Button
            color="soft-primary"
            size="sm"
            onClick={() => navigator.clipboard.writeText(`<script src="${props.script}"></script>`)}
          >
            <i className="mdi mdi-content-copy"></i>
          </Button>
        </td>
        <td className="text-center">
          <div className="d-flex flex-column align-items-center gap-1">
            <span
              className={`badge ${formData.chatbotsModuleEnabled ? "bg-success" : "bg-secondary"} px-3 py-1`}
            >
              Chatbot
            </span>
            <span
              className={`badge ${formData.voiceAssistantModuleEnabled ? "bg-success" : "bg-secondary"} px-3 py-1`}
            >
              Voice
            </span>
            <small className="text-muted">
              {props.maxChatbotsAllowed} | {props.maxVoiceAssistantsAllowed}
            </small>
          </div>
        </td>
        <td>
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-success"
              onClick={(e) => {
                e.preventDefault();
                toggleModal();
              }}
            >
              <i className="mdi mdi-pencil font-size-20"></i>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={(e) => {
                e.preventDefault();
                deleteCompany(props.id, props.adminID);
              }}
            >
              <i className="mdi mdi-delete font-size-20"></i>
            </Link>
          </div>
        </td>
      </tr>
    </>
  );
}